"use client"

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

// Default OneTrust category id for Algolia insights / analytics. C0002
// is the standard "Performance Cookies" group in the Trimble OneTrust
// configuration. Profiles may override via
// NEXT_PUBLIC_ALGOLIA_CONSENT_CATEGORY.
export const DEFAULT_ALGOLIA_CONSENT_CATEGORY = "C0002"

// Storage keys that the Algolia path owns:
//  - "_ALGOLIA"            cookie set by search-insights when
//                           init({ useCookie: true }) is called
//  - "nav-search-recents"   localStorage key used by
//                           createLocalStorageRecentSearchesPlugin
//                           in AutocompleteSearchBox.tsx
//  - "searches"             legacy localStorage key written by
//                           storeSearchLocally in NavSearchMethods.tsx
export const ALGOLIA_COOKIE_NAME = "_ALGOLIA"
export const ALGOLIA_LOCAL_STORAGE_KEYS = ["nav-search-recents", "searches"]

declare global {
  interface Window {
    OneTrust?: {
      IsAlertBoxClosedAndValid?: () => boolean
    }
    OnetrustActiveGroups?: string
  }
}

interface AlgoliaConsentContextType {
  hasAlgoliaConsent: boolean
}

const AlgoliaConsentContext = createContext<AlgoliaConsentContextType>({
  hasAlgoliaConsent: false,
})

export function useAlgoliaConsent(): AlgoliaConsentContextType {
  return useContext(AlgoliaConsentContext)
}

const parseOneTrustGroups = (rawGroups: string): Set<string> =>
  new Set(
    rawGroups
      .split(",")
      .map(group => group.trim())
      .filter(Boolean),
  )

/**
 * Returns true when the given OneTrust active-groups string includes the
 * supplied category id (in any of its accepted forms: bare number, padded
 * `C0002`, or already-normalized). Mirrors the behaviour of the helper
 * in VwoConsent.tsx so the two stay consistent.
 */
export function resolveHasCategoryConsent(
  rawGroups: string,
  consentCategoryId: string,
): boolean {
  if (!consentCategoryId) return false
  const groups = parseOneTrustGroups(rawGroups)
  const normalized = consentCategoryId.replace(/^C0*/, "")
  const candidates = new Set([
    consentCategoryId,
    normalized,
    `C${normalized.padStart(4, "0")}`,
  ])
  for (const candidate of candidates) {
    if (groups.has(candidate)) return true
  }
  return false
}

/**
 * Reads OneTrust consent for a category directly from the OptanonConsent
 * cookie (format `"C0001:1,C0002:1,C0004:0"`, `:1` meaning accepted). The
 * cookie is already present on first render for a returning visitor,
 * whereas `window.OnetrustActiveGroups` isn't populated until the OneTrust
 * script (loaded afterInteractive) finishes initializing — a hook that
 * waits for the `OneTrustGroupsUpdated` event alone can miss it entirely if
 * that event fires before the effect subscribes. Mirrors
 * ConsentProvider.tsx's hasOneTrustTargetingConsent().
 */
export function resolveHasCookieCategoryConsent(
  groupsCookieValue: string,
  consentCategoryId: string,
): boolean {
  if (!consentCategoryId) return false
  const normalized = consentCategoryId.replace(/^C0*/, "")
  const candidates = new Set([consentCategoryId, `C${normalized.padStart(4, "0")}`])
  return groupsCookieValue.split(",").some(entry => {
    const [id, state] = entry.split(":")
    return state?.trim() === "1" && candidates.has(id?.trim())
  })
}

function extractOptanonGroupsCookieValue(cookieString: string): string {
  const match = cookieString.match(/(?:^|;\s*)OptanonConsent=([^;]+)/)
  if (!match) return ""
  return new URLSearchParams(match[1]).get("groups") || ""
}

export function hasOneTrustCookieConsent(consentCategoryId: string): boolean {
  if (typeof document === "undefined" || !consentCategoryId) return false
  return resolveHasCookieCategoryConsent(
    extractOptanonGroupsCookieValue(document.cookie),
    consentCategoryId,
  )
}

/**
 * Deletes the `_ALGOLIA` cookie across every plausible host scope so a
 * revoke fires regardless of which subdomain set it. Cookies have no
 * read API for the original Domain attribute, so we expire on the exact
 * hostname plus every parent suffix (e.g. `civilconstruction.trimble.com`
 * and `.trimble.com`) — covers the multi-site case where future sites
 * share a parent domain.
 */
export function clearAlgoliaCookie(): void {
  if (typeof document === "undefined") return
  const hostname = window.location.hostname
  const parts = hostname.split(".")
  const domains = new Set<string>()
  domains.add(hostname)
  domains.add(`.${hostname}`)
  for (let i = 1; i < parts.length - 1; i++) {
    domains.add(`.${parts.slice(i).join(".")}`)
  }
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT"
  for (const domain of domains) {
    document.cookie = `${ALGOLIA_COOKIE_NAME}=; expires=${expired}; path=/; domain=${domain}`
  }
  document.cookie = `${ALGOLIA_COOKIE_NAME}=; expires=${expired}; path=/`
}

export function purgeAlgoliaLocalStorage(): void {
  if (typeof window === "undefined") return
  try {
    for (const key of ALGOLIA_LOCAL_STORAGE_KEYS) {
      window.localStorage.removeItem(key)
    }
  } catch {
    // Browser may block storage access in private mode — ignore.
  }
}

interface AlgoliaConsentProviderProps {
  value: boolean
  children: React.ReactNode
}

/**
 * Internal provider. The actual consent state is owned by
 * AlgoliaInsightsBootstrap which listens to OneTrust; this component is
 * a thin wrapper so descendants can subscribe via useAlgoliaConsent().
 */
export function AlgoliaConsentProvider({
  value,
  children,
}: AlgoliaConsentProviderProps) {
  const ctxValue = useMemo(() => ({ hasAlgoliaConsent: value }), [value])
  return (
    <AlgoliaConsentContext.Provider value={ctxValue}>
      {children}
    </AlgoliaConsentContext.Provider>
  )
}

/**
 * Hook that returns the live OneTrust-derived consent state for the
 * Algolia category. Used by AlgoliaInsightsBootstrap (and exported for
 * any non-provider consumers that need to drive their own effects).
 *
 * When `oneTrustId` is empty (dev / preview / draft mode) consent is
 * granted immediately, matching the rest of the codebase's treatment of
 * a missing OneTrust ID.
 */
export function useResolvedAlgoliaConsent({
  oneTrustId,
  consentCategoryId,
  bypassConsentCheck,
  isDevelopment,
}: {
  oneTrustId?: string | null
  consentCategoryId?: string | null
  bypassConsentCheck?: boolean
  isDevelopment?: boolean
}): boolean {
  const effectiveCategory =
    consentCategoryId || DEFAULT_ALGOLIA_CONSENT_CATEGORY
  // Mirrors ConsentProvider.tsx's isOneTrustEnabled gate so the two
  // stay in lock-step: OneTrust drives consent only in non-dev profiles
  // that have an ID configured and aren't explicitly bypassed.
  const oneTrustEnabled =
    !isDevelopment && !bypassConsentCheck && !!oneTrustId

  // Lazy initializer runs synchronously on first render (before any
  // effect), so a returning visitor's already-set cookie is picked up
  // immediately instead of only after OneTrustGroupsUpdated fires.
  const [hasConsent, setHasConsent] = useState<boolean>(
    () => !oneTrustEnabled || hasOneTrustCookieConsent(effectiveCategory),
  )

  useEffect(() => {
    if (!oneTrustEnabled) {
      setHasConsent(true)
      return
    }
    if (typeof window === "undefined") return

    const check = () => {
      const rawGroups = String(window.OnetrustActiveGroups || "")
      const isValid = Boolean(
        window.OneTrust?.IsAlertBoxClosedAndValid &&
          window.OneTrust.IsAlertBoxClosedAndValid(),
      )
      const globalGranted =
        isValid && resolveHasCategoryConsent(rawGroups, effectiveCategory)
      setHasConsent(globalGranted || hasOneTrustCookieConsent(effectiveCategory))
    }

    check()
    window.addEventListener("OneTrustGroupsUpdated", check)
    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", check)
    }
  }, [oneTrustEnabled, effectiveCategory])

  return hasConsent
}
